import {
  LOGS_PERMISSIONS,
  PAYMENTS_SECTIONS_ROUTES,
  PS_MANAGEMENT_PERMISSIONS,
  PaymentsPermissions,
  TRANSACTIONS_PERMISSIONS,
  CASHIER_SETTINGS_PERMISSIONS,
  PSP_SORTING_PERMISSIONS,
  PSP_TAGS_PERMISSIONS,
  CURRENCY_PERMISSIONS,
  LIMIT_EXCEPTIONS_UPLOAD_PERMISSIONS,
} from '@/models/app-internal/permissions.model';
import { defineStore } from 'pinia';

interface PermissionsStore {
  paymentsPermissions: PaymentsPermissions;
}

export const usePermissionsStore = defineStore({
  id: 'permissions',
  state: (): PermissionsStore => ({
    paymentsPermissions: {
      [PAYMENTS_SECTIONS_ROUTES.PS_MANAGEMENT]: {
        // ====================================
        // === GEO PAYMENT SYSTEMS SETTINGS ===
        // ====================================
        [PS_MANAGEMENT_PERMISSIONS.GET_GEO_PAYMENT_METHOD_LIST_BY_COUNTRY]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_LIST_SORTING]: false,

        // SINGLE METHODS (устаревшие и новые)
        [PS_MANAGEMENT_PERMISSIONS.GET_GEO_PAYMENT_METHOD_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.CREATE_GEO_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.CREATE_GEO_SINGLE_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_DEPOSIT_AND_WITHDRAW]: false,
        [PS_MANAGEMENT_PERMISSIONS.DELETE_GEO_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD]: false,

        // GEO PAYMENT METHODS DEPOSIT LIMITS
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_GEO_PAYMENT_METHOD_DEPOSIT_LIMITS]: false,
        [PS_MANAGEMENT_PERMISSIONS.GET_GEO_PAYMENT_METHOD_DEPOSIT_LIMITS]: false,
        [PS_MANAGEMENT_PERMISSIONS.GET_GEO_PAYMENT_METHOD_DEPOSIT_LIMITS_LIST]: false,

        // ================================
        // === MULTI PAYMENT METHODS ======
        // ================================
        [PS_MANAGEMENT_PERMISSIONS.GET_MULTI_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.CREATE_MULTI_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_MULTI_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.DELETE_MULTI_PAYMENT_METHOD]: false,

        // MULTI PAYMENT METHODS — MANAGEMENT
        [PS_MANAGEMENT_PERMISSIONS.GET_AVAILABLE_BILLING_METHOD_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.GET_ASSOCIATED_BILLING_METHOD_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.ASSOCIATE_BILLING_METHODS]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_ASSOCIATED_BILLIND_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.DISASSOCIATE_BILLING_METHOD]: false,

        // ================================
        // === PAYMENT SYSTEMS GEO LIST ===
        // ================================
        [PS_MANAGEMENT_PERMISSIONS.GET_PAYMENT_METHOD_GEO_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.ADD_PAYMENT_METHOD_GEO]: false,

        // =======================================
        // === GLOBAL PAYMENT SYSTEMS SETTINGS ===
        // =======================================
        [PS_MANAGEMENT_PERMISSIONS.GET_GLOBAL_PAYMENT_METHOD_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.GET_GLOBAL_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.CREATE_GLOBAL_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.DELETE_GLOBAL_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_GLOBAL_PAYMENT_METHOD]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_GLOBAL_PAYMENT_METHOD_DEPOSIT_AND_WITHDRAW]: false,

        // ================================================
        // === GEO METHOD EXCLUDED DEPOSIT LIMIT SETTINGS =
        // ================================================
        [PS_MANAGEMENT_PERMISSIONS.GET_METHOD_EXTENDED_LIMIT_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.GET_METHOD_GENERAL_LIMIT_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.CREATE_METHOD_EXTENDED_LIMIT]: false,
        [PS_MANAGEMENT_PERMISSIONS.DELETE_METHOD_EXTENDED_LIMIT]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_METHOD_EXTENDED_LIMIT]: false,

        // =======================================
        // === PAYMENT METHOD CONVERTABLE CURRENCY SETTINGS ===
        // =======================================
        [PS_MANAGEMENT_PERMISSIONS.GET_BILLING_PROVIDER_CONVERTABLE_CURRENCY_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.GET_PAYMENT_METHOD_CURRENCY_CONVERTER_LIST]: false,
        [PS_MANAGEMENT_PERMISSIONS.SYNC_PAYMENT_METHOD_CURRENCY_CONVERTER_LIST]: false,

        // =======================================
        // === PAYMENT METHOD CONVERTABLE CURRENCY SETTINGS ===
        // =======================================
        [PS_MANAGEMENT_PERMISSIONS.GET_PAYMENT_METHOD_UNAVAILABLE_HOURS_SETTINGS]: false,
        [PS_MANAGEMENT_PERMISSIONS.UPDATE_PAYMENT_METHOD_UNAVAILABLE_HOURS_SETTINGS]: false,
      },
      [PAYMENTS_SECTIONS_ROUTES.TRANSACTIONS]: {
        [TRANSACTIONS_PERMISSIONS.GET_PAYMENT_LIST]: false,
        [TRANSACTIONS_PERMISSIONS.GET_PAYMENT_LIST_TOTAL]: false,
        [TRANSACTIONS_PERMISSIONS.GET_PAYMENT_LIST_CSV]: false,
      },
      [PAYMENTS_SECTIONS_ROUTES.CASHIER_SETTINGS]: {
        [CASHIER_SETTINGS_PERMISSIONS.GET_CONFIG_LIST]: false,
        [CASHIER_SETTINGS_PERMISSIONS.GET_CONFIG]: false,
        [CASHIER_SETTINGS_PERMISSIONS.UPDATE_CASHIER_CONFIG]: false,
        [CASHIER_SETTINGS_PERMISSIONS.CREATE_CASHIER_CONFIG]: false,
      },
      [PAYMENTS_SECTIONS_ROUTES.PSP_SORTING]: {
        [PSP_SORTING_PERMISSIONS.GET_PSP_SORTING_LIST]: false,
      },
      [PAYMENTS_SECTIONS_ROUTES.CURRENCY]: {
        [CURRENCY_PERMISSIONS.GET_CURRENCY_LIST]: false,
        [CURRENCY_PERMISSIONS.UPDATE_CURRENCY_LIST]: false,
      },
      [PAYMENTS_SECTIONS_ROUTES.PSP_TAGS]: {
        [PSP_TAGS_PERMISSIONS.GET_PSP_TAG]: false,
        [PSP_TAGS_PERMISSIONS.GET_PSP_TAG_LIST]: false,
        [PSP_TAGS_PERMISSIONS.CREATE_PSP_TAG]: false,
        [PSP_TAGS_PERMISSIONS.UPDATE_PSP_TAG]: false,
        [PSP_TAGS_PERMISSIONS.DELETE_PSP_TAG]: false,
      },
      [PAYMENTS_SECTIONS_ROUTES.LOGS]: {
        [LOGS_PERMISSIONS.GET_BILLING_SETTINGS_CHANGE_HISTORY]: false,
        [LOGS_PERMISSIONS.GET_BILLING_SETTINGS_CHANGE_HISTORY_CSV]: false,
        [LOGS_PERMISSIONS.GET_BILLING_SETTINGS_CHANGE_FILTER_LIST]: false,
      },
      [PAYMENTS_SECTIONS_ROUTES.LIMIT_EXCEPTIONS_UPLOAD]: {
        [LIMIT_EXCEPTIONS_UPLOAD_PERMISSIONS.UPLOAD_LIMIT_EXCEPTIONS]: false,
      },
    },
  }),
  getters: {
    psManagementPermissions(state) {
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.PS_MANAGEMENT];
    },
    pspSortingPermissions(state) {
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.PSP_SORTING];
    },
    transactionsPermissions(state) {
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.TRANSACTIONS];
    },
    cashierSettingsPermissions(state) {
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.CASHIER_SETTINGS];
    },
    logsPermissions(state) {
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.LOGS];
    },
    isTransactionsDownloadCvsAllowed(): boolean {
      return this.transactionsPermissions[TRANSACTIONS_PERMISSIONS.GET_PAYMENT_LIST_CSV];
    },
    isLogsFiltersAllowed(): boolean {
      return this.logsPermissions[LOGS_PERMISSIONS.GET_BILLING_SETTINGS_CHANGE_FILTER_LIST];
    },
    isLogsDownloadCvsAllowed(): boolean {
      return this.logsPermissions[LOGS_PERMISSIONS.GET_BILLING_SETTINGS_CHANGE_HISTORY_CSV];
    },
    isGetCurrencyListAllowed(state: PermissionsStore): boolean {
    // eslint-disable-next-line max-len
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.CURRENCY][CURRENCY_PERMISSIONS.GET_CURRENCY_LIST];
    },
    isUpdateCurrencyListAllowed(state: PermissionsStore): boolean {
    // eslint-disable-next-line max-len
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.CURRENCY][CURRENCY_PERMISSIONS.UPDATE_CURRENCY_LIST];
    },
    isUploadLimitExceptionsAllowed(state: PermissionsStore): boolean {
      // eslint-disable-next-line max-len
      return state.paymentsPermissions[PAYMENTS_SECTIONS_ROUTES.LIMIT_EXCEPTIONS_UPLOAD][LIMIT_EXCEPTIONS_UPLOAD_PERMISSIONS.UPLOAD_LIMIT_EXCEPTIONS];
    },
  },
  actions: {
    setPermissions(permissions: PaymentsPermissions) {
      this.paymentsPermissions = permissions;
    },
    checkPermission(permission: string) {
      const actualSections = Object.keys(this.paymentsPermissions);

      return actualSections.some((section) => {
        return this.paymentsPermissions[section as PAYMENTS_SECTIONS_ROUTES][permission];
      });
    },
  },
});
